<template>
  <div class="auth-wrapper auth-v1">
    <div class="auth-inner m-0">
      <b-card class="mb-0">
        <b-link class="brand-logo">
          <b-img
            class="img-fluid"
            :src="appLogoImage"
            alt="logo"
          />
        </b-link>

        <b-card-title
          title-tag="h2"
          class="font-weight-bold mb-1"
        >
          Set a new password
        </b-card-title>

        <b-card-text class="mb-2">
          Choose a new password for
          <strong>{{ form.email || 'your account' }}</strong>.
        </b-card-text>

        <b-alert
          v-if="!form.token || !form.email"
          show
          variant="danger"
          class="mb-2"
        >
          This reset link is invalid or incomplete. Please request a new one.
        </b-alert>

        <b-form
          class="auth-reset-form mt-2"
          @submit.prevent="submit"
        >
          <b-form-group
            label="Email"
            label-for="reset-email"
          >
            <b-form-input
              id="reset-email"
              v-model="form.email"
              name="reset-email"
              type="email"
              disabled
            />
          </b-form-group>

          <b-form-group label-for="reset-password">
            <div class="d-flex justify-content-between">
              <label for="reset-password">New password</label>
            </div>
            <b-input-group class="input-group-merge">
              <b-form-input
                id="reset-password"
                v-model="form.password"
                class="form-control-merge"
                :type="passwordFieldType"
                name="reset-password"
                placeholder="············"
                autocomplete="new-password"
                autofocus
              />
              <b-input-group-append is-text>
                <feather-icon
                  class="cursor-pointer"
                  :icon="passwordToggleIcon"
                  @click="togglePasswordVisibility"
                />
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <b-form-group label-for="reset-password-confirmation">
            <div class="d-flex justify-content-between">
              <label for="reset-password-confirmation">Confirm password</label>
            </div>
            <b-input-group class="input-group-merge">
              <b-form-input
                id="reset-password-confirmation"
                v-model="form.password_confirmation"
                class="form-control-merge"
                :type="passwordFieldType"
                name="reset-password-confirmation"
                placeholder="············"
                autocomplete="new-password"
              />
            </b-input-group>
          </b-form-group>

          <small
            v-if="errorMessage"
            class="text-danger d-block mb-1"
          >{{ errorMessage }}</small>

          <b-button
            type="submit"
            variant="primary"
            block
            :disabled="isLoading || !form.token || !form.email"
          >
            <b-spinner
              v-if="isLoading"
              label="Spinning"
              small
            />
            <span v-else>Change password</span>
          </b-button>

          <p class="mt-3 mb-0">
            <b-link :to="{ name: 'recovery' }">
              Request a new reset link
            </b-link>
            <span class="mx-50">·</span>
            <b-link :to="{ name: 'login' }">
              Back to sign-in
            </b-link>
          </p>
        </b-form>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BLink,
  BFormGroup,
  BFormInput,
  BInputGroupAppend,
  BInputGroup,
  BCardText,
  BCardTitle,
  BImg,
  BForm,
  BButton,
  BSpinner,
  BCard,
} from 'bootstrap-vue'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { toastSuccess, toastFailure } from '@/libs/toastification'
import { mapActions } from 'vuex'
import { $themeConfig } from '@themeConfig'

export default {
  components: {
    BAlert,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BSpinner,
    BCard,
  },
  mixins: [togglePasswordVisibility],
  data() {
    const { appLogoImage } = $themeConfig.app
    return {
      form: {
        password: '',
        password_confirmation: '',
        email: '',
        token: '',
      },
      errorMessage: '',
      isLoading: false,
      appLogoImage,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  created() {
    this.form.token = String(this.$route.params.token || '').trim()
    const rawEmail = this.$route.query.email
    this.form.email = rawEmail
      ? decodeURIComponent(String(rawEmail).replace(/\+/g, '%20')).trim()
      : ''
  },
  methods: {
    ...mapActions({
      reset: 'auth/reset',
    }),
    async submit() {
      this.errorMessage = ''

      if (!this.form.token || !this.form.email) {
        this.errorMessage = 'This reset link is invalid or incomplete.'
        return
      }

      if (!this.form.password || this.form.password.length < 8) {
        this.errorMessage = 'Password must be at least 8 characters.'
        return
      }

      if (this.form.password !== this.form.password_confirmation) {
        this.errorMessage = 'Password confirmation does not match.'
        return
      }

      this.isLoading = true
      try {
        await this.reset({
          email: this.form.email,
          token: this.form.token,
          password: this.form.password,
          password_confirmation: this.form.password_confirmation,
        })
        toastSuccess('Your password has been changed. Please sign in.')
        this.$router.replace({ name: 'login' })
      } catch (error) {
        const errors = error?.response?.data?.errors
        if (errors && typeof errors === 'object') {
          const firstKey = Object.keys(errors)[0]
          this.errorMessage = errors[firstKey]?.[0] || 'The given data was invalid.'
        } else {
          this.errorMessage = error?.response?.data?.message
            || 'Could not reset your password. The link may have expired.'
        }
        toastFailure(this.errorMessage)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
